import React from 'react';
import { PageConnector } from '../types';
import { ArrowDown, ArrowUp } from 'lucide-react';

interface PageConnectorNodeProps {
  connector: PageConnector;
  selected?: boolean;
  onSelect?: (id: string) => void;
  onDelete?: (id: string) => void;
  onUpdatePosition?: (id: string, x: number, y: number) => void;
  onUpdateLabel?: (id: string, label: string) => void;
  scale: number;
  pan: { x: number; y: number };
}

export const PageConnectorNode: React.FC<PageConnectorNodeProps> = ({
  connector,
  selected = false,
  onSelect,
  onDelete,
  onUpdatePosition,
  onUpdateLabel,
  scale,
  pan
}) => {
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragOffset, setDragOffset] = React.useState({ x: 0, y: 0 });
  const [isEditing, setIsEditing] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const color = connector.color || '#3b82f6';
  const isOut = connector.direction === 'out';

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    
    if (onSelect) onSelect(connector.id);
    
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    setDragOffset({
      x: (e.clientX - rect.left) / scale,
      y: (e.clientY - rect.top) / scale
    });
    setIsDragging(true);
  };

  const handleMouseMove = React.useCallback((e: MouseEvent) => {
    if (!isDragging || !onUpdatePosition) return;
    
    const x = (e.clientX - pan.x) / scale - dragOffset.x;
    const y = (e.clientY - pan.y) / scale - dragOffset.y;
    
    onUpdatePosition(connector.id, Math.max(0, x), Math.max(0, y));
  }, [isDragging, onUpdatePosition, connector.id, scale, pan, dragOffset]);

  const handleMouseUp = React.useCallback(() => {
    setIsDragging(false);
  }, []);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().slice(0, 3); // Máximo 3 caracteres
    if (onUpdateLabel) {
      onUpdateLabel(connector.id, value);
    }
  };

  const handleLabelBlur = () => {
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === 'Escape') {
      setIsEditing(false);
    }
  };

  return (
    <div
      style={{
        position: 'absolute',
        left: connector.x,
        top: connector.y,
        transform: 'translate(-50%, -50%)',
        cursor: isDragging ? 'grabbing' : 'grab',
        zIndex: selected ? 100 : 50
      }}
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
    >
      {/* Círculo principal */}
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          backgroundColor: 'white',
          border: `3px solid ${color}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: selected ? `0 0 0 3px ${color}40, 0 4px 6px rgba(0,0,0,0.1)` : '0 2px 4px rgba(0,0,0,0.1)',
          transition: 'all 0.2s',
          userSelect: 'none'
        }}
      >
        {/* Ícone de direção */}
        <div style={{ marginTop: -2 }}>
          {isOut ? (
            <ArrowDown size={14} color={color} strokeWidth={3} />
          ) : (
            <ArrowUp size={14} color={color} strokeWidth={3} />
          )}
        </div>
        
        {/* Label editável */}
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={connector.label}
            onChange={handleLabelChange}
            onBlur={handleLabelBlur}
            onKeyDown={handleKeyDown}
            style={{
              width: 30,
              height: 20,
              textAlign: 'center',
              fontSize: 16,
              fontWeight: 'bold',
              color: color,
              border: 'none',
              background: 'transparent',
              outline: 'none',
              marginTop: -2
            }}
            maxLength={3}
          />
        ) : (
          <span
            style={{
              fontSize: 16,
              fontWeight: 'bold',
              color: color,
              marginTop: -2
            }}
          >
            {connector.label}
          </span>
        )}
      </div>

      {/* Botão de deletar (aparece quando selecionado) */}
      {selected && onDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(connector.id);
          }}
          style={{
            position: 'absolute',
            top: -8,
            right: -8,
            width: 20,
            height: 20,
            borderRadius: '50%',
            backgroundColor: '#ef4444',
            border: '2px solid white',
            color: 'white',
            fontSize: 12,
            fontWeight: 'bold',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}
        >
          ×
        </button>
      )}

      {/* Indicador de direção na borda */}
      <div
        style={{
          position: 'absolute',
          bottom: -12,
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: 9,
          fontWeight: 'bold',
          color: color,
          backgroundColor: 'white',
          padding: '2px 6px',
          borderRadius: 4,
          border: `1px solid ${color}`,
          whiteSpace: 'nowrap'
        }}
      >
        {isOut ? 'SAÍDA' : 'ENTRADA'}
      </div>
    </div>
  );
};
